import { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Button } from '@/components/Button';
import { Input } from '@/components/Input';
import { ThemeToggle } from '@/components/ThemeToggle';
import { api, ApiError, ensureCsrf } from '@/api/client';
import { keys } from '@/api/queryKeys';
import { useToast } from '@/components/Toast';
import type { LoginResponse, MessageResponse, MeResponse } from '@/api/types';

interface LocationState {
  from?: string;
}

export function Login() {
  const navigate = useNavigate();
  const location = useLocation();
  const qc = useQueryClient();
  const toast = useToast();
  const from = (location.state as LocationState | null)?.from ?? '/event-surveillance';

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [code, setCode] = useState('');
  const [challengeId, setChallengeId] = useState<string | null>(null);
  // Which second factor the user is presenting. A backup code is NOT a TOTP
  // code with a longer field: it is 14 characters of base32 in three groups
  // (`cb12-h33j-7p4q`) and it goes to a different endpoint, because
  // `/totp/verify` caps `code` at 8 and rejects it with a 400 before the logic
  // ever sees it. Until 2026-09-25 this page offered only the 6-digit input,
  // `maxLength={6}`, so a backup code could not even be TYPED — the codes were
  // issued, stored and single-use-checked, and were unusable at the one moment
  // they exist for. Nobody had hit it because nobody had enrolled yet.
  const [mode, setMode] = useState<'totp' | 'backup'>('totp');

  const login = useMutation({
    mutationFn: async () => {
      await ensureCsrf();
      return api<LoginResponse>('/auth/login', {
        method: 'POST',
        body: { email, password },
      });
    },
    onSuccess: async (data) => {
      if (data.status === 'totp_required') {
        setChallengeId(data.challenge_id);
        return;
      }
      const me = await api<MeResponse>('/auth/me');
      qc.setQueryData(keys.auth.me, me);
      navigate(from, { replace: true });
    },
    onError: (err: unknown) => {
      if (err instanceof ApiError) {
        toast.push({ tone: 'danger', title: 'Sign in failed', description: err.message });
      }
    },
  });

  const verifyTotp = useMutation({
    mutationFn: async () => {
      await ensureCsrf();
      return api<MessageResponse>(
        mode === 'backup' ? '/auth/backup-code/verify' : '/auth/totp/verify',
        { method: 'POST', body: { challenge_id: challengeId, code } },
      );
    },
    onSuccess: async () => {
      const me = await api<MeResponse>('/auth/me');
      qc.setQueryData(keys.auth.me, me);
      navigate(from, { replace: true });
    },
    onError: (err: unknown) => {
      if (err instanceof ApiError) {
        toast.push({ tone: 'danger', title: 'Invalid code', description: err.message });
      }
    },
  });

  return (
    <div className="auth-shell">
      <div className="auth-card">
        <div className="auth-card__brand">
          <span className="auth-card__brand-mark" aria-hidden />
          <span>Polymarket Surveillance</span>
          <div className="spacer" />
          <ThemeToggle />
        </div>

        {!challengeId ? (
          <>
            <h2 className="auth-card__title">Sign in</h2>
            <p className="auth-card__subtitle">Use your approved institutional email.</p>
            <form
              className="stack-5"
              onSubmit={(e) => {
                e.preventDefault();
                login.mutate();
              }}
            >
              <Input
                label="Email"
                type="email"
                name="email"
                autoComplete="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />
              <Input
                label="Password"
                type="password"
                name="password"
                autoComplete="current-password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
              />
              <Button variant="primary" type="submit" block disabled={login.isPending}>
                {login.isPending ? 'Signing in…' : 'Continue'}
              </Button>
            </form>
            <div className="auth-card__footer">
              <Link to="/reset-password">Forgot password?</Link>
              <span className="subtle"> · </span>
              <Link to="/register">Request access</Link>
            </div>
          </>
        ) : (
          <>
            <h2 className="auth-card__title">Two-factor code</h2>
            <p className="auth-card__subtitle">
              {mode === 'totp'
                ? 'Enter the 6-digit code from your authenticator app.'
                : 'Enter one of the recovery codes you saved when you turned on two-factor. Each works once.'}
            </p>
            <form
              className="stack-5"
              onSubmit={(e) => {
                e.preventDefault();
                verifyTotp.mutate();
              }}
            >
              {mode === 'totp' ? (
                <Input
                  label="Code"
                  name="code"
                  inputMode="numeric"
                  maxLength={6}
                  required
                  autoFocus
                  placeholder="123 456"
                  value={code}
                  onChange={(e) => setCode(e.target.value)}
                />
              ) : (
                <Input
                  label="Recovery code"
                  name="code"
                  // No numeric hint and a longer cap: these are base32 with
                  // dashes, and `maxLength={6}` is what made them untypable.
                  maxLength={32}
                  required
                  autoFocus
                  autoCapitalize="none"
                  autoCorrect="off"
                  spellCheck={false}
                  placeholder="abcd-efgh-jkmn"
                  value={code}
                  onChange={(e) => setCode(e.target.value)}
                />
              )}
              <Button variant="primary" type="submit" block disabled={verifyTotp.isPending}>
                {verifyTotp.isPending ? 'Verifying…' : 'Verify'}
              </Button>
              <Button
                variant="ghost"
                type="button"
                block
                onClick={() => {
                  setMode(mode === 'totp' ? 'backup' : 'totp');
                  setCode('');
                }}
              >
                {mode === 'totp' ? 'Use a recovery code instead' : 'Use my authenticator app'}
              </Button>
              <Button
                variant="ghost"
                type="button"
                block
                onClick={() => {
                  setChallengeId(null);
                  setCode('');
                  setMode('totp');
                }}
              >
                Back
              </Button>
            </form>
          </>
        )}
      </div>
    </div>
  );
}
